import { AppConfig } from "~/modal/appconfig.model";

const APP_CONFIG_URL = "/assets/config.json";
const APP_CONFIG_DEPLOYED_URL = "/browser/assets/config.json";
const APP_CONFIG_GLOBAL_KEY = "__WELEXE_APP_CONFIG__";

type GlobalWithConfig = typeof globalThis & {
  [APP_CONFIG_GLOBAL_KEY]?: AppConfig;
};

export function getPreloadedConfig(): AppConfig | null {
  const globalRef = globalThis as GlobalWithConfig;
  return globalRef[APP_CONFIG_GLOBAL_KEY] ?? null;
}

export function setPreloadedConfig(config: AppConfig): void {
  const globalRef = globalThis as GlobalWithConfig;
  globalRef[APP_CONFIG_GLOBAL_KEY] = config;
}

function getConfigPath(): string {
  if (typeof window === "undefined") {
    return APP_CONFIG_URL;
  }

  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  return isLocal ? APP_CONFIG_URL : APP_CONFIG_DEPLOYED_URL;
}

export async function preloadAppConfig(): Promise<AppConfig> {
  const cached = getPreloadedConfig();
  if (cached) {
    return cached;
  }

  if (typeof fetch === "undefined") {
    const emptyConfig = {} as AppConfig;
    setPreloadedConfig(emptyConfig);
    return emptyConfig;
  }

  try {
    const response = await fetch(getConfigPath(), { cache: "no-cache" });

    if (!response.ok) {
      console.error(`Failed to load config: ${response.status} ${response.statusText}`);
      const emptyConfig = {} as AppConfig;
      setPreloadedConfig(emptyConfig);
      return emptyConfig;
    }

    const config = (await response.json()) as AppConfig;
    setPreloadedConfig(config);
    return config;
  } catch (error) {
    console.error('Failed to load application configuration:', error);
    const emptyConfig = {} as AppConfig;
    setPreloadedConfig(emptyConfig);
    return emptyConfig;
  }
}